import React, { useEffect, useMemo, useRef } from "react";
import { Form, Modal } from "react-bootstrap";
import Button from "../command/Button/Button";
import "./informationCaptureCard.scss";
import InputForm from "../command/input/inputField";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";

// RHF field names must only contain alphanumeric chars and underscores.
// API fieldKeys can contain ?, ', - etc. which break RHF's internal path resolution.
const toSafeFieldName = (key: string): string =>
  key.replace(/[^a-zA-Z0-9_]/g, "_");

interface CustomField {
  id: number;
  uuid: string;
  storeUuid: string;
  fieldKey: string;
  label: string;
  type: string;
  isRequired: boolean;
  sortOrder: number;
}

interface ExistingFieldValue {
  uuid: string;
  fieldKey: string;
  fieldLabel: string;
  fieldValue: string;
}

interface CustomFieldsModalProps {
  show: boolean;
  onHide: () => void;
  onSubmit: (values: any) => void;
  customFields: CustomField[];
  existingValues?: ExistingFieldValue[];
  loading?: boolean;
}

const CustomFieldsModal: React.FC<CustomFieldsModalProps> = ({
  show,
  onHide,
  onSubmit,
  customFields,
  existingValues = [],
  loading = false,
}) => {
  const { t } = useTranslation();
  const prevShowRef = useRef(show);

  const validationSchema = useMemo(() => {
    const schemaFields: any = {};

    customFields.forEach((field) => {
      const safeName = toSafeFieldName(field.fieldKey);
      if (field.isRequired) {
        schemaFields[safeName] = yup
          .string()
          .required(`${field.label} is required`);
      } else {
        schemaFields[safeName] = yup.string();
      }
    });

    return yup.object().shape(schemaFields);
  }, [customFields]);

  const defaultValues = useMemo(() => {
    const defaults: any = {};
    customFields.forEach((field) => {
      const safeName = toSafeFieldName(field.fieldKey);
      const existingValue = existingValues.find(
        (val) => val.fieldKey === field.fieldKey
      );
      defaults[safeName] = existingValue?.fieldValue || "";
    });
    return defaults;
  }, [customFields, existingValues]);

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "all",
  });

  const handleFormSubmit = (values: any) => {
    // Map safe field names back to original fieldKeys for API submission
    const mappedValues: any = {};
    customFields.forEach((field) => {
      const safeName = toSafeFieldName(field.fieldKey);
      mappedValues[field.fieldKey] = values[safeName];
    });
    onSubmit(mappedValues);
  };

  useEffect(() => {
    if (show && !prevShowRef.current) {
      reset(defaultValues);
    }
    prevShowRef.current = show;
  }, [show, defaultValues, reset]);

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby="custom-fields-modal"
      centered
      className="billing-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title id="custom-fields-modal">
         Almost done — please complete the following
        </Modal.Title>
       
      </Modal.Header>
      <Modal.Body>
        <div className="billing-modal-wrapper">
          <Form className="billing-modal-form">
            {customFields.map((field) => {
              const safeName = toSafeFieldName(field.fieldKey);
              return (
                <InputForm
                  key={field.fieldKey}
                  inputId={safeName}
                  inputLable={field.label}
                  inputType="text"
                  required={field.isRequired}
                  inputPlaceholder={field.label}
                  register={register(safeName)}
                  error={(errors[safeName] as any)?.message}
                />
              );
            })}
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          btnText={loading ? "Submitting..." : t("continue")}
          disabled={!isValid || loading}
          onClick={handleSubmit(handleFormSubmit)}
        />
      </Modal.Footer>
      <p className="subtitle-text fs-12 text-center securetext">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" >
<path d="M5 13C5 12.4696 5.21071 11.9609 5.58579 11.5858C5.96086 11.2107 6.46957 11 7 11H17C17.5304 11 18.0391 11.2107 18.4142 11.5858C18.7893 11.9609 19 12.4696 19 13V19C19 19.5304 18.7893 20.0391 18.4142 20.4142C18.0391 20.7893 17.5304 21 17 21H7C6.46957 21 5.96086 20.7893 5.58579 20.4142C5.21071 20.0391 5 19.5304 5 19V13Z" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M11 16C11 16.2652 11.1054 16.5196 11.2929 16.7071C11.4804 16.8946 11.7348 17 12 17C12.2652 17 12.5196 16.8946 12.7071 16.7071C12.8946 16.5196 13 16.2652 13 16C13 15.7348 12.8946 15.4804 12.7071 15.2929C12.5196 15.1054 12.2652 15 12 15C11.7348 15 11.4804 15.1054 11.2929 15.2929C11.1054 15.4804 11 15.7348 11 16Z" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
<path d="M8 11V7C8 5.93913 8.42143 4.92172 9.17157 4.17157C9.92172 3.42143 10.9391 3 12 3C13.0609 3 14.0783 3.42143 14.8284 4.17157C15.5786 4.92172 16 5.93913 16 7V11" stroke="#333333" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>Your information is secure</p>
    </Modal>
  );
};

export default CustomFieldsModal;
